import { CPLAT_API_ROUTES, usePost } from '@/api';
import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { Checkbox } from '@/components/atoms/PcCheckbox';
import { cn } from '@/utils/tailwind';
import { useCallback, useEffect, useState } from 'react';

const DONT_SHOW_BANNER_KEY = 'dont_show_dialog_until';

const AlertDialog = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [dontShowChecked, setDontShowChecked] = useState(false);
  const [popupImage, setPopupImage] = useState<string | null>(null);

  const [targetUrl, setTargetUrl] = useState<string>();
  // 1. 외부 브라우저 여부를 저장할 state 추가
  const [isExternal, setIsExternal] = useState(false);

  const { mutate: getpopUpInfo } = usePost(CPLAT_API_ROUTES.getPopupInfo);

  const fetchPopupInfo = useCallback(() => {
    getpopUpInfo(
      {},
      {
        onSuccess: (response) => {
          const dataList = response?.data.data;
          if (dataList && dataList.length > 0) {
            const firstImage = dataList[0].ImageList?.[0] || null;
            const url = dataList[0].TargetUrl || null;
            // API 응답에서 IsExternalBrowser 값 추출
            const externalBrowser = dataList[0].IsExternalBrowser;

            if (url) setTargetUrl(url);
            // state에 저장
            setIsExternal(externalBrowser);

            if (firstImage) {
              setPopupImage(firstImage);
              setShowDialog(true);
            } else {
              setShowDialog(false);
            }
          } else {
            setShowDialog(false);
          }
        },
        onError: (error) => {
          setShowDialog(false);
        },
      },
    );
  }, [getpopUpInfo]);

  useEffect(() => {
    const untilStr = localStorage.getItem(DONT_SHOW_BANNER_KEY);

    if (!untilStr) {
      fetchPopupInfo();
      return;
    }

    const untilDate = new Date(untilStr);
    if (isNaN(untilDate.getTime())) {
      fetchPopupInfo();
      return;
    }

    if (new Date() > untilDate) {
      fetchPopupInfo();
    } else {
      setShowDialog(false);
    }
  }, [fetchPopupInfo]);

  useEffect(() => {
    if (!showDialog && dontShowChecked) {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      localStorage.setItem(DONT_SHOW_BANNER_KEY, d.toISOString());
    }
  }, [showDialog, dontShowChecked]);

  const handleClose = () => {
    setShowDialog(false);
  };

  // 2. 클릭 핸들러 로직 분리
  const handleImageClick = () => {
    if (!targetUrl) return;

    if (isExternal) {
      // IsExternalBrowser가 true인 경우: TargetUrl 그대로 새 탭 열기
      window.open(targetUrl, '_blank');
    } else {
      // IsExternalBrowser가 false인 경우: 현재 도메인 유지 + 경로만 교체
      try {
        // API에서 받은 URL 파싱 (예: https://app.sellerbox.io/surveys/1)
        const urlObj = new URL(targetUrl);
        // pathname만 추출 (예: /surveys/1)
        const path = urlObj.pathname;
        
        // 현재 origin (예: http://localhost:5173) + 추출한 path 조합
        const internalUrl = `${window.location.origin}${path}`;
        
        window.open(internalUrl, '_blank');
      } catch (e) {
        // 만약 targetUrl이 완전한 URL 형태가 아니라면(상대경로 등) fallback 처리
        console.error('URL parsing failed', e);
        window.open(targetUrl, '_blank');
      }
    }
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent noCloseBtn className="max-w-[450px] gap-0 p-0">
        {popupImage && (
          <div
            className={cn('flex justify-center', { 'cursor-pointer': targetUrl })}
            onClick={handleImageClick} // 핸들러 연결
          >
            <img
              src={popupImage}
              alt="공지 이미지"
              className="max-h-[400px] max-w-full rounded-lg object-contain"
            />
          </div>
        )}

        <div className="flex items-center justify-between p-[20px]">
          <label className="flex cursor-pointer select-none items-center Body5M14">
            <Checkbox
              checked={dontShowChecked}
              onCheckedChange={(checked) => setDontShowChecked(!!checked)}
              className="mr-2"
            />
            오늘 하루동안 보지 않기
          </label>

          <Button size="small" onClick={handleClose} type="button">
            닫기
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AlertDialog;